import { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import ColumnSelect from './ColumnSelect';
import OptionSelect from './OptionSelect';
import { rankColumnsForRole, type ColumnMeta } from './utils/rankColumnsForRole';
import buildPrompt, { type BuilderBindings } from './utils/buildPrompt';
import { trackClient } from '@/lib/obs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';

export interface ChartBuilderCardProps {
  chartId: string | null | undefined;
  columns: Array<{ name: string }>;
  rolesSpec: { x: { multiple: true }, y: { multiple: true }, series: { multiple: false }, facet: { multiple: false } };
  onBindingsChange: (bindings: BuilderBindings, promptText: string) => void;
  onSend: (promptText: string, bindings?: BuilderBindings) => void;
  columnMetaByName?: Record<string, ColumnMeta>;
  tourHighlightActive?: boolean;
  fingerprintReady?: boolean; // New prop to control auto-open when fingerprint is ready
}

export function ChartBuilderCard({ chartId, columns, rolesSpec: _rolesSpec, onBindingsChange, onSend: _onSend, columnMetaByName, tourHighlightActive = false, fingerprintReady = false }: ChartBuilderCardProps) {
  const [bindings, setBindings] = useState<BuilderBindings>({});
  const [rolesOpen, setRolesOpen] = useState<boolean>(false);
  const [advancedOpen, setAdvancedOpen] = useState<boolean>(false);

  const allNames = useMemo(() => columns.map((c) => c.name), [columns]);

  const ranked = useMemo(() => {
    return {
      x: rankColumnsForRole(columns, 'x', columnMetaByName),
      y: rankColumnsForRole(columns, 'y', columnMetaByName),
      series: rankColumnsForRole(columns, 'series', columnMetaByName),
      facet: rankColumnsForRole(columns, 'facet', columnMetaByName),
    };
  }, [columns, columnMetaByName]);

  const promptText = useMemo(() => buildPrompt(chartId, bindings), [chartId, bindings]);

  useEffect(() => {
    onBindingsChange(bindings, promptText);
  }, [bindings, promptText, onBindingsChange]);

  const topN = 6;

  const seriesCardinalityHint = useMemo(() => {
    const s = bindings.series;
    if (!s) return null;
    const meta = columnMetaByName?.[s];
    const card = meta?.categorical?.cardinality ?? meta?.unique ?? null;
    if (card != null && card > 50) return `${card} groups. Consider Facet or Filter.`;
    return null;
  }, [bindings.series, columnMetaByName]);

  const chartInlineHint = useMemo(() => {
    const id = String(chartId || '').toLowerCase();
    if ((id.includes('pie') || id.includes('doughnut')) && bindings.x) {
      const x0 = Array.isArray(bindings.x) ? bindings.x[0] : bindings.x;
      const meta = columnMetaByName?.[String(x0)];
      const card = meta?.categorical?.cardinality ?? meta?.unique ?? null;
      if (card != null && card > 8) return 'Pie gets hard to read with many categories.';
    }
    return null;
  }, [chartId, bindings.x, columnMetaByName]);

  // Auto-expand roles when fingerprint is ready OR when a chart is picked/changed
  useEffect(() => {
    if (fingerprintReady || chartId) setRolesOpen(true);
  }, [fingerprintReady, chartId]);

  // Listen for auto-fill roles event from TourManager
  useEffect(() => {
    const handleAutoFill = (event: CustomEvent<{ x: string[]; y: string[]; series: string }>) => {
      const { x, y, series } = event.detail;
      
      // Map column names (case-insensitive) to find matching columns
      const findColumn = (name: string): string | null => {
        const normalized = name.toLowerCase();
        return allNames.find(c => c.toLowerCase() === normalized) || null;
      };

      const siteCol = x.length > 0 ? findColumn(x[0]) : null;
      const valueCol = y.length > 0 ? findColumn(y[0]) : null;
      const treatmentCol = findColumn(series);

      if (siteCol && valueCol && treatmentCol) {
        // Update bindings directly
        setBindings({
          x: [siteCol],
          y: [valueCol],
          series: treatmentCol,
        });
        console.log('[ChartBuilderCard] Auto-filled roles:', { x: siteCol, y: valueCol, series: treatmentCol });
      } else {
        console.warn('[ChartBuilderCard] Could not find all required columns for auto-fill', {
          requested: { x, y, series },
          available: allNames,
          found: { siteCol, valueCol, treatmentCol },
        });
      }
    };

    window.addEventListener('lumi:auto-fill-roles', handleAutoFill as EventListener);
    return () => {
      window.removeEventListener('lumi:auto-fill-roles', handleAutoFill as EventListener);
    };
  }, [allNames]);

  return (
    <Card className={`p-3 space-y-3 max-w-[640px] mx-auto ${tourHighlightActive ? 'tour-highlight-active' : ''}`} data-lumi="chart-builder">
      <div data-lumi="roles-panel">
      {chartInlineHint && (
        <div className="text-[11px] text-yellow-800 bg-yellow-50 border border-yellow-200 px-2 py-1 rounded">{chartInlineHint}</div>
      )}
      <Collapsible open={rolesOpen} onOpenChange={setRolesOpen}>
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">Roles</div>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" aria-label="Toggle roles" className="h-7 px-2 text-xs [&[data-state=open]>svg]:rotate-180">
              <ChevronDown className="ml-1 h-3 w-3 opacity-70" />
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <div className="space-y-3 pt-2">
            {/* Simple roles: X, Y, Group by */}
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <div data-lumi="role-x">
                <div className="text-xs mb-0.5">X</div>
                <ColumnSelect
                  role="x"
                  columns={allNames}
                  recommendedTop={ranked.x.slice(0, topN)}
                  value={bindings.x ?? []}
                  multiple
                  onChange={(v, source) => {
                    const arr = Array.isArray(v) ? v : (v ? [String(v)] : []);
                    setBindings((b) => ({ ...b, x: arr.length ? arr : undefined }));
                    try { trackClient('builder_role_changed', { role: 'x', col: v, source }); } catch {}
                  }}
                />
              </div>
              <div data-lumi="role-y">
                <div className="text-xs mb-0.5">Y</div>
                <ColumnSelect
                  role="y"
                  columns={allNames}
                  recommendedTop={ranked.y.slice(0, topN)}
                  value={bindings.y ?? []}
                  multiple
                  onChange={(v, source) => {
                    const arr = Array.isArray(v) ? v : (v ? [String(v)] : []);
                    setBindings((b) => ({ ...b, y: arr.length ? arr : undefined }));
                    try { trackClient('builder_role_changed', { role: 'y', col: v, source }); } catch {}
                  }}
                />
              </div>
              <div data-lumi="role-series">
                <div className="text-xs mb-0.5">Group by</div>
                <ColumnSelect
                  role="series"
                  columns={allNames}
                  recommendedTop={ranked.series.slice(0, topN)}
                  value={bindings.series ?? null}
                  onChange={(v, source) => {
                    const s = Array.isArray(v) ? (v[0] || null) : (v as string | null);
                    setBindings((b) => ({ ...b, series: s || undefined }));
                    try { trackClient('builder_role_changed', { role: 'series', col: s, source }); } catch {}
                  }}
                />
                {seriesCardinalityHint && (
                  <div className="text-[11px] text-muted-foreground mt-1">{seriesCardinalityHint}</div>
                )}
              </div>
            </div>

            {/* Advanced roles: Facet, Uncertainty, N */}
            <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
              <div className="border-t border-border pt-3">
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" aria-label="Toggle advanced roles" className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground -ml-2 [&[data-state=open]>svg]:rotate-180">
                    <ChevronDown className="mr-1 h-3 w-3 opacity-70" />
                    Advanced
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2 pt-2">
                    <div>
                      <div className="text-xs mb-0.5 text-muted-foreground">Facet</div>
                      <ColumnSelect
                        role="facet"
                        columns={allNames}
                        recommendedTop={ranked.facet.slice(0, topN)}
                        value={bindings.facet ?? null}
                        onChange={(v, source) => {
                          const s = Array.isArray(v) ? (v[0] || null) : (v as string | null);
                          setBindings((b) => ({ ...b, facet: s || undefined }));
                          try { trackClient('builder_role_changed', { role: 'facet', col: s, source }); } catch {}
                        }}
                      />
                    </div>
                    <div>
                      <div className="text-xs mb-0.5 text-muted-foreground">Uncertainty</div>
                      <OptionSelect
                        value={bindings.uncertainty ?? null}
                        options={[
                          { value: 'CL', label: 'Confidence Interval (CL)' },
                          { value: 'SE', label: 'Standard Error (SE)' },
                          { value: 'SD', label: 'Standard Deviation (SD)' },
                        ] as const}
                        placeholder="Pick uncertainty metric"
                        onChange={(v) => {
                          setBindings((b) => ({ ...b, uncertainty: (v as any) || undefined }));
                          try { trackClient('builder_role_changed', { role: 'uncertainty', value: v }); } catch {}
                        }}
                      />
                    </div>
                    <div>
                      <div className="text-xs mb-0.5 text-muted-foreground">N</div>
                      <OptionSelect
                        value={bindings.n ?? null}
                        options={[
                          { value: 'none', label: 'None' },
                          { value: 'subtitle', label: 'Subtitle' },
                          { value: 'tick', label: 'Axis ticks' },
                          { value: 'text-annotation', label: 'Text annotation overlay' },
                        ] as const}
                        placeholder="Where to show N labels"
                        onChange={(v) => {
                          const next = (v === 'none' || v == null) ? undefined : (v as any);
                          setBindings((b) => ({ ...b, n: next }));
                          try { trackClient('builder_role_changed', { role: 'n', value: v }); } catch {}
                        }}
                      />
                    </div>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          </div>
        </CollapsibleContent>
      </Collapsible>
      </div>
    </Card>
  );
}

export default ChartBuilderCard;


